import { useEffect } from 'react';

/**
 * Auth0 Callback Page for Teams Authentication
 *
 * This page is opened in a popup by Teams SDK.
 * It receives the OAuth token from Auth0 and sends it back to the main Teams tab.
 */
const AuthCallback = () => {
  useEffect(() => {
    const notifyTeamsFailure = (message: string) => {
      if (window.microsoftTeams?.authentication?.notifyFailure) {
        window.microsoftTeams.authentication.notifyFailure(message);
      }
    };

    const notifyTeamsSuccess = (result: string) => {
      if (window.microsoftTeams?.authentication?.notifySuccess) {
        window.microsoftTeams.authentication.notifySuccess(result);
      } else {
        console.error('Teams SDK not available in callback');
      }
    };

    const processAuthSuccess = (params: URLSearchParams) => {
      const accessToken = params.get('access_token');
      const idToken = params.get('id_token');
      const expiresIn = params.get('expires_in') || '3600';

      if (!accessToken) {
        console.error('No access token in callback');
        notifyTeamsFailure('No access token received');
        return;
      }

      const result = JSON.stringify({
        access_token: accessToken,
        id_token: idToken,
        expires_in: expiresIn
      });

      console.log('Sending auth result to Teams');
      notifyTeamsSuccess(result);
    };

    const processAuthError = (error: string, errorDescription: string) => {
      console.error('Auth error:', error, errorDescription);
      notifyTeamsFailure(errorDescription || error);
    };

    const handleCallback = () => {
      try {
        console.log('Auth callback page loaded');

        // Get token from URL hash (Auth0 implicit flow)
        const hash = window.location.hash.substring(1);
        const params = new URLSearchParams(hash);

        const error = params.get('error');
        const errorDescription = params.get('error_description');
        const accessToken = params.get('access_token');

        console.log('Auth callback params:', { accessToken: !!accessToken, error });

        if (error) {
          processAuthError(error, errorDescription || '');
          return;
        }

        processAuthSuccess(params);
      } catch (err) {
        console.error('Error in auth callback:', err);
        const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
        notifyTeamsFailure(errorMessage);
      }
    };

    // Initialize Teams SDK and handle callback
    const initTeams = async () => {
      if (window.microsoftTeams) {
        await window.microsoftTeams.app.initialize();
        console.log('Teams SDK initialized in callback page');
        handleCallback();
      } else {
        console.error('Teams SDK not loaded');
      }
    };

    initTeams();
  }, []);

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      flexDirection: 'column',
      fontFamily: 'Segoe UI, sans-serif'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '4px solid #f3f3f3',
        borderTop: '4px solid #3498db',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />
      <p style={{ marginTop: '20px', color: '#666' }}>Completing authentication...</p>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AuthCallback;
